import { type MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Card,
  Center,
  Group,
  Loader,
  Paper,
  ScrollArea,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import { Building2, ChevronRight, Globe2, MapPin, Minus, Plus, Users } from "lucide-react";
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from "react-simple-maps";
import dayjs from "dayjs";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";

type CountryAnalytics = {
  country: string;
  count: number;
  latestJoinedAt: string | Date;
  cities: {
    city: string;
    state: string | null;
    count: number;
    latestJoinedAt: string | Date;
  }[];
};

type RecentCountry = {
  country: string;
  city: string | null;
  state: string | null;
  joinedAt: string | Date;
  companyName: string;
  gtid: string;
};

type TooltipState = {
  x: number;
  y: number;
  title: string;
  detail: string;
} | null;

const GEO_URL = "https://cdn.jsdelivr.net/npm/react-simple-maps@0.10.0/topojson-maps/world-110m.json";

const COUNTRY_COORDINATES: Record<string, [number, number]> = {
  Japan: [138.3, 36.2],
  Thailand: [101.0, 15.9],
  Taiwan: [121.0, 23.7],
  Singapore: [103.8, 1.35],
  India: [79.6, 22.9],
  "South Korea": [127.8, 36.4],
  "Sri Lanka": [80.7, 7.9],
  Malaysia: [102.0, 4.2],
  Indonesia: [118.0, -2.5],
  Vietnam: [108.3, 14.1],
  Philippines: [122.8, 12.9],
  "China (Mainland)": [104.2, 35.9],
  "China (Hong Kong)": [114.2, 22.3],
  Bangladesh: [90.3, 23.7],
  Nepal: [84.1, 28.4],
  "United Arab Emirates (UAE)": [54.4, 24.3],
  Oman: [55.9, 21.5],
  Kuwait: [47.5, 29.3],
  "Saudi Arabia": [45.1, 23.9],
  Turkey: [35.2, 39.0],
  Egypt: [30.8, 26.8],
  Qatar: [51.2, 25.4],
  Bahrain: [50.6, 26.1],
  Israel: [35.0, 31.0],
  Austria: [14.5, 47.5],
  Belgium: [4.5, 50.5],
  Bulgaria: [25.5, 42.7],
  Croatia: [15.2, 45.1],
  Cyprus: [33.4, 35.1],
  "Czech Republic": [15.5, 49.8],
  Denmark: [9.5, 56.2],
  Estonia: [25.0, 58.6],
  Finland: [26.0, 64.0],
  France: [2.2, 46.2],
  Germany: [10.4, 51.2],
  Greece: [22.0, 39.0],
  Hungary: [19.5, 47.2],
  Ireland: [-8.2, 53.4],
  Italy: [12.5, 42.8],
  Latvia: [24.6, 56.9],
  Lithuania: [23.9, 55.2],
  Luxembourg: [6.1, 49.8],
  Malta: [14.4, 35.9],
  Netherlands: [5.3, 52.1],
  Norway: [8.5, 60.5],
  Poland: [19.1, 52.0],
  Portugal: [-8.2, 39.4],
  Romania: [24.9, 45.9],
  "Russia Federation": [89.0, 61.5],
  Slovakia: [19.7, 48.7],
  Slovenia: [14.9, 46.1],
  Spain: [-3.7, 40.4],
  Sweden: [18.6, 60.1],
  "United Kingdom": [-2.0, 54.0],
  "South Africa": [24.0, -29.0],
  Kenya: [37.9, 0.1],
  Morocco: [-7.1, 31.8],
  Australia: [133.8, -25.3],
  "New Zealand": [174.9, -40.9],
  USA: [-98.6, 39.8],
  Canada: [-106.3, 56.1],
  Mexico: [-102.5, 23.6],
  Brazil: [-53.2, -10.3],
};

const MAP_COUNTRY_NAMES: Record<string, string[]> = {
  "China (Mainland)": ["China"],
  "China (Hong Kong)": ["Hong Kong"],
  "Czech Republic": ["Czechia", "Czech Republic"],
  "Russia Federation": ["Russia"],
  "United Arab Emirates (UAE)": ["United Arab Emirates"],
  USA: ["United States of America", "United States", "United States (USA)"],
};

const COUNTRY_FLAGS: Record<string, string> = {
  Japan: "🇯🇵",
  Thailand: "🇹🇭",
  Taiwan: "🇹🇼",
  Singapore: "🇸🇬",
  India: "🇮🇳",
  "South Korea": "🇰🇷",
  "Sri Lanka": "🇱🇰",
  Malaysia: "🇲🇾",
  Indonesia: "🇮🇩",
  Vietnam: "🇻🇳",
  Philippines: "🇵🇭",
  "China (Mainland)": "🇨🇳",
  "China (Hong Kong)": "🇭🇰",
  Bangladesh: "🇧🇩",
  Nepal: "🇳🇵",
  "United Arab Emirates (UAE)": "🇦🇪",
  Oman: "🇴🇲",
  Kuwait: "🇰🇼",
  "Saudi Arabia": "🇸🇦",
  Turkey: "🇹🇷",
  Egypt: "🇪🇬",
  Qatar: "🇶🇦",
  Bahrain: "🇧🇭",
  Israel: "🇮🇱",
  Austria: "🇦🇹",
  Belgium: "🇧🇪",
  Bulgaria: "🇧🇬",
  Croatia: "🇭🇷",
  Cyprus: "🇨🇾",
  "Czech Republic": "🇨🇿",
  Denmark: "🇩🇰",
  Estonia: "🇪🇪",
  Finland: "🇫🇮",
  France: "🇫🇷",
  Germany: "🇩🇪",
  Greece: "🇬🇷",
  Hungary: "🇭🇺",
  Ireland: "🇮🇪",
  Italy: "🇮🇹",
  Latvia: "🇱🇻",
  Lithuania: "🇱🇹",
  Luxembourg: "🇱🇺",
  Malta: "🇲🇹",
  Netherlands: "🇳🇱",
  Norway: "🇳🇴",
  Poland: "🇵🇱",
  Portugal: "🇵🇹",
  Romania: "🇷🇴",
  "Russia Federation": "🇷🇺",
  Slovakia: "🇸🇰",
  Slovenia: "🇸🇮",
  Spain: "🇪🇸",
  Sweden: "🇸🇪",
  "United Kingdom": "🇬🇧",
  "South Africa": "🇿🇦",
  Kenya: "🇰🇪",
  Morocco: "🇲🇦",
  Australia: "🇦🇺",
  "New Zealand": "🇳🇿",
  USA: "🇺🇸",
  Canada: "🇨🇦",
  Mexico: "🇲🇽",
  Brazil: "🇧🇷",
};

const CITY_MARKER_OFFSETS: [number, number][] = [
  [0, 0],
  [2.2, 1.4],
  [-2.1, 1.2],
  [1.8, -1.7],
  [-1.7, -1.5],
  [3.2, 0],
  [-3.1, 0],
  [0, 2.7],
];

function formatLocation(city: string | null, state: string | null, country: string) {
  return [city, state, country].filter(Boolean).join(", ");
}

function formatJoinedDate(value: string | Date) {
  return dayjs(value).format("MMM D, YYYY");
}

function getCountryMapNames(country: string) {
  return [country, ...(MAP_COUNTRY_NAMES[country] ?? [])].map((name) => name.toLowerCase());
}

function getGeographyName(properties: Record<string, unknown> | undefined) {
  const rawName = properties?.name ?? properties?.NAME ?? properties?.NAME_LONG ?? properties?.ADMIN;
  return String(rawName ?? "").toLowerCase();
}

function getCountryFill(count: number, maxCount: number) {
  if (count >= 10) return "#8b5cf6";
  if (count >= 6) return "#a78bfa";
  if (count >= 3) return "#bda8ff";
  if (count >= 2) return "#d0c2ff";
  if (maxCount > 1) return "#ded5ff";
  return "#eee9ff";
}

function AnimatedStatCard({
  icon,
  label,
  value,
  suffix,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  suffix?: string;
}) {
  return (
    <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
      <Paper
        radius="xl"
        p="lg"
        style={{
          border: "1px solid rgba(124, 58, 237, 0.12)",
          background: "rgba(255, 255, 255, 0.72)",
          boxShadow: "0 18px 45px rgba(91, 33, 182, 0.08)",
          backdropFilter: "blur(16px)",
        }}
      >
        <Group justify="space-between" wrap="nowrap">
          <div>
            <Text size="xs" c="dimmed" fw={700} tt="uppercase">
              {label}
            </Text>
            <Text fw={800} size="28px" lh={1.1} c="#35185f">
              <CountUp end={value} duration={1.1} separator="," />
              {suffix}
            </Text>
          </div>
          <ThemeIcon variant="gradient" gradient={{ from: "violet.5", to: "indigo.5" }} size={48} radius="lg">
            {icon}
          </ThemeIcon>
        </Group>
      </Paper>
    </motion.div>
  );
}

function CountriesWorldMap({
  countries,
  onTooltipChange,
}: {
  countries: CountryAnalytics[];
  onTooltipChange: (tooltip: TooltipState) => void;
}) {
  const [zoom, setZoom] = useState(1);
  const countryNames = useMemo(() => {
    const names = new Set<string>();
    countries.forEach((country) => getCountryMapNames(country.country).forEach((name) => names.add(name)));
    return names;
  }, [countries]);
  const maxCountryCount = Math.max(1, ...countries.map((country) => country.count));
  const countryByMapName = useMemo(() => {
    const rows = new Map<string, CountryAnalytics>();
    countries.forEach((country) => {
      getCountryMapNames(country.country).forEach((name) => rows.set(name, country));
    });
    return rows;
  }, [countries]);

  const cityMarkers = countries.flatMap((country) => {
    const baseCoordinates = COUNTRY_COORDINATES[country.country];
    if (!baseCoordinates) return [];

    return country.cities.slice(0, 8).map((city, index) => {
      const [lonOffset, latOffset] = CITY_MARKER_OFFSETS[index % CITY_MARKER_OFFSETS.length];
      return {
        country: country.country,
        city: city.city,
        state: city.state,
        count: city.count,
        coordinates: [baseCoordinates[0] + lonOffset, baseCoordinates[1] + latOffset] as [number, number],
      };
    });
  });

  return (
    <Card
      radius={28}
      p={{ base: "md", md: "xl" }}
      style={{
        minHeight: 560,
        position: "relative",
        overflow: "hidden",
        border: "1px solid rgba(124, 58, 237, 0.16)",
        background:
          "linear-gradient(135deg, #dceeff 0%, #e7e4ff 48%, #d8f0ff 100%), radial-gradient(circle at 15% 10%, rgba(124, 58, 237, 0.22), transparent 30%)",
        boxShadow: "0 24px 70px rgba(67, 56, 202, 0.16)",
      }}
    >
      <Group justify="space-between" mb="sm" style={{ position: "relative", zIndex: 2 }}>
        <div>
          <Group gap="xs">
            <ThemeIcon color="violet" variant="light" radius="md">
              <Globe2 size={18} />
            </ThemeIcon>
            <Text fw={800} size="lg" c="#241442">
              Platform geography
            </Text>
          </Group>
          <Text size="sm" c="dimmed" mt={4}>
            Highlighted countries represent company headquarters on the platform.
          </Text>
        </div>

        <Group gap={8}>
          <ActionIcon
            variant="white"
            color="violet"
            radius="md"
            onClick={() => setZoom((value) => Math.max(1, value - 0.35))}
          >
            <Minus size={16} />
          </ActionIcon>
          <ActionIcon
            variant="white"
            color="violet"
            radius="md"
            onClick={() => setZoom((value) => Math.min(4, value + 0.35))}
          >
            <Plus size={16} />
          </ActionIcon>
        </Group>
      </Group>

      <Box style={{ position: "relative", height: 455 }}>
        <ComposableMap projectionConfig={{ scale: 150 }} style={{ width: "100%", height: "100%" }}>
          <ZoomableGroup
            zoom={zoom}
            center={[8, 14]}
            translateExtent={[
              [-100, -80],
              [1060, 640],
            ]}
          >
            <Geographies geography={GEO_URL}>
              {({ geographies }: { geographies: any[] }) =>
                geographies.map((geo) => {
                  const name = getGeographyName(geo.properties);
                  const isActive = countryNames.has(name);
                  const activeCountry = countryByMapName.get(name);
                  const activeFill = activeCountry
                    ? getCountryFill(activeCountry.count, maxCountryCount)
                    : "#d6e3ef";
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onMouseMove={(event: MouseEvent<SVGPathElement>) => {
                        if (!activeCountry) return;
                        onTooltipChange({
                          x: event.clientX,
                          y: event.clientY,
                          title: activeCountry.country,
                          detail: `${activeCountry.count} ${activeCountry.count === 1 ? "company" : "companies"}`,
                        });
                      }}
                      onMouseLeave={() => onTooltipChange(null)}
                      style={{
                        default: {
                          fill: isActive ? activeFill : "#d6e3ef",
                          stroke: "#f8fbff",
                          strokeWidth: 0.7,
                          outline: "none",
                        },
                        hover: {
                          fill: isActive ? "#a78bfa" : "#c3d5e5",
                          stroke: "#f8fbff",
                          strokeWidth: 0.7,
                          outline: "none",
                        },
                        pressed: {
                          fill: "#8b5cf6",
                          outline: "none",
                        },
                      }}
                    />
                  );
                })
              }
            </Geographies>

            {cityMarkers.map((marker, index) => {
              return (
                <Marker
                  key={`${marker.country}-${marker.city}-${marker.state ?? ""}-${index}`}
                  coordinates={marker.coordinates}
                  onMouseMove={(event: MouseEvent<SVGGElement>) =>
                    onTooltipChange({
                      x: event.clientX,
                      y: event.clientY,
                      title: `${COUNTRY_FLAGS[marker.country] ?? "🌐"} ${formatLocation(
                        marker.city,
                        marker.state,
                        marker.country
                      )}`,
                      detail: `${marker.count} ${marker.count === 1 ? "company" : "companies"}`,
                    })
                  }
                  onMouseLeave={() => onTooltipChange(null)}
                >
                  <motion.g
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.35 }}
                  >
                    <circle r={5} fill="rgba(109, 40, 217, 0.14)" />
                    <circle r={3.5} fill="#ffffff" stroke="#7c3aed" strokeWidth={1.1} />
                    <path
                      d="M0 -5.8 C-2.8 -5.8 -5 -3.6 -5 -0.9 C-5 3 0 7 0 7 C0 7 5 3 5 -0.9 C5 -3.6 2.8 -5.8 0 -5.8 Z"
                      fill="#6d28d9"
                    />
                    <circle r={1.5} cy={-1} fill="#ffffff" />
                  </motion.g>
                </Marker>
              );
            })}
          </ZoomableGroup>
        </ComposableMap>
      </Box>
      <Text size="xs" c="#5b4b7a" mt="xs">
        Showing {cityMarkers.length} city/state marker{cityMarkers.length === 1 ? "" : "s"} across {countries.length}{" "}
        countr{countries.length === 1 ? "y" : "ies"}.
      </Text>
    </Card>
  );
}

export default function AdminCountriesMapPage() {
  const [countries, setCountries] = useState<CountryAnalytics[]>([]);
  const [recentCountries, setRecentCountries] = useState<RecentCountry[]>([]);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const [totalCountries, setTotalCountries] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tooltip, setTooltip] = useState<TooltipState>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await trpc.admin.companyLocationAnalytics.query({});
      setCountries(result.countries);
      setRecentCountries(result.recentCountries);
      setTotalCompanies(result.totalCompanies);
      setTotalCountries(result.totalCountries);
    } catch (error) {
      handleTrpcError(error);
      setCountries([]);
      setRecentCountries([]);
      setTotalCompanies(0);
      setTotalCountries(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const topCountry = countries[0];

  return (
    <Box
      p="md"
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f8fbff 0%, #f4f0ff 48%, #eef7ff 100%)",
      }}
    >
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <div>
            <Group gap="sm">
              <ThemeIcon color="violet" variant="light" size={48} radius="lg">
                <Globe2 size={26} />
              </ThemeIcon>
              <div>
                <Title order={2} c="#241442">
                  Countries on our platform
                </Title>
                <Text c="dimmed" size="sm" mt={2}>
                  Explore company headquarters by country, city, and optional state.
                </Text>
              </div>
            </Group>
          </div>
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <AnimatedStatCard icon={<Globe2 size={23} />} label="Countries joined" value={totalCountries} />
          <AnimatedStatCard icon={<Building2 size={23} />} label="Companies matched" value={totalCompanies} />
          <AnimatedStatCard icon={<Users size={23} />} label="Top country count" value={topCountry?.count ?? 0} />
        </SimpleGrid>

        {loading ? (
          <Center h={420}>
            <Loader color="violet" />
          </Center>
        ) : countries.length === 0 ? (
          <Paper radius="xl" p="xl" withBorder>
            <Text c="dimmed">No companies found for this location.</Text>
          </Paper>
        ) : (
          <>
            <CountriesWorldMap countries={countries} onTooltipChange={setTooltip} />

            <Paper
              radius="xl"
              p="md"
              style={{
                border: "1px solid rgba(124, 58, 237, 0.10)",
                background: "rgba(255, 255, 255, 0.78)",
                boxShadow: "0 18px 45px rgba(91, 33, 182, 0.08)",
                backdropFilter: "blur(16px)",
              }}
            >
              <Group justify="space-between" mb="md">
                <div>
                  <Text fw={800} c="#241442">
                    Recently joined countries
                  </Text>
                  <Text size="sm" c="dimmed">
                    Based on the latest company created date per country.
                  </Text>
                </div>
                <ChevronRight size={18} color="#7c3aed" />
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 5 }}>
                {recentCountries.map((item, index) => (
                  <motion.div
                    key={`${item.country}-${item.gtid}`}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.32, delay: index * 0.04 }}
                  >
                    <Card radius="lg" p="sm" style={{ background: "rgba(255, 255, 255, 0.82)" }}>
                      <Group gap="sm" wrap="nowrap">
                        <Text size="28px" lh={1}>
                          {COUNTRY_FLAGS[item.country] ?? "🌐"}
                        </Text>
                        <div style={{ minWidth: 0 }}>
                          <Text fw={800} size="sm" truncate c="#241442">
                            {item.country}
                          </Text>
                          <Text size="xs" c="dimmed" truncate>
                            {formatLocation(item.city, item.state, item.country)}
                          </Text>
                          <Text size="xs" c="dimmed">
                            Joined {formatJoinedDate(item.joinedAt)}
                          </Text>
                        </div>
                      </Group>
                    </Card>
                  </motion.div>
                ))}
              </SimpleGrid>
            </Paper>

            <Paper radius="xl" p="md" withBorder style={{ background: "rgba(255, 255, 255, 0.76)" }}>
              <Text fw={800} c="#241442">
                Location breakdown
              </Text>
              <Text size="sm" c="dimmed" mb="md">
                Country totals with the top city and state combinations.
              </Text>
              <ScrollArea>
                <Table striped highlightOnHover verticalSpacing="sm" miw={760}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Country</Table.Th>
                      <Table.Th>Companies</Table.Th>
                      <Table.Th>Top cities / states</Table.Th>
                      <Table.Th>Latest join</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {countries.map((item) => (
                      <Table.Tr key={item.country}>
                        <Table.Td>
                          <Group gap="xs">
                            <Text size="lg">{COUNTRY_FLAGS[item.country] ?? "🌐"}</Text>
                            <Text fw={700}>{item.country}</Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Badge color="violet" variant="light">
                            {item.count}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={6}>
                            {item.cities.slice(0, 4).map((cityItem) => (
                              <Badge
                                key={`${item.country}-${cityItem.city}-${cityItem.state ?? ""}`}
                                variant="outline"
                                color="gray"
                                leftSection={<MapPin size={11} />}
                              >
                                {formatLocation(cityItem.city, cityItem.state, "")} ({cityItem.count})
                              </Badge>
                            ))}
                          </Group>
                        </Table.Td>
                        <Table.Td>{formatJoinedDate(item.latestJoinedAt)}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Paper>
          </>
        )}
      </Stack>

      {tooltip ? (
        <Box
          style={{
            position: "fixed",
            left: tooltip.x + 14,
            top: tooltip.y + 14,
            zIndex: 1000,
            pointerEvents: "none",
            padding: "10px 12px",
            borderRadius: 14,
            color: "#241442",
            border: "1px solid rgba(124, 58, 237, 0.16)",
            background: "rgba(255, 255, 255, 0.88)",
            boxShadow: "0 16px 36px rgba(67, 56, 202, 0.16)",
            backdropFilter: "blur(14px)",
          }}
        >
          <Text fw={800} size="sm">
            {tooltip.title}
          </Text>
          <Text size="xs" c="dimmed">
            {tooltip.detail}
          </Text>
        </Box>
      ) : null}
    </Box>
  );
}
